import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
  SheetFooter,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Loader2, Sparkles, Store, Warehouse, Truck, ShoppingCart,
  CheckCircle2, MessageSquare, Plus, Package,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const TYPE_META = {
  mayorista: { icon: Warehouse, label: "Mayorista", color: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900" },
  supermercado: { icon: ShoppingCart, label: "Supermercado", color: "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900" },
  especialista: { icon: Store, label: "Especialista", color: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900" },
  distribuidor: { icon: Truck, label: "Distribuidor", color: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900" },
  otros: { icon: Package, label: "Otros", color: "bg-muted text-foreground border-border" },
};

/**
 * Drawer de sugerencias de proveedores para un ingrediente concreto.
 * Al abrirse, pregunta a la IA por 3 opciones reales + checklist de
 * negociación. El usuario puede "Añadir a mi lista" y se crea un doc
 * en /suppliers vinculando al restaurante.
 */
export function SupplierSuggestionsDrawer({
  open, onOpenChange, ingredientName, restaurantId, onSupplierCreated,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(null);
  const [added, setAdded] = useState(new Set());

  useEffect(() => {
    if (!open || !restaurantId || !ingredientName) {
      setData(null);
      setAdded(new Set());
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.suggestSuppliersForIngredient(restaurantId, ingredientName)
      .then(res => { if (!cancelled) setData(res); })
      .catch(err => {
        if (!cancelled) toast.error(err?.message || "No se pudieron obtener sugerencias");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, restaurantId, ingredientName]);

  const handleAdd = async (supplier) => {
    setAdding(supplier.name);
    try {
      const notes = [
        `[${supplier.type}] ${supplier.specialty}`,
        `Precio estimado: ${supplier.price_min.toFixed(2)}–${supplier.price_max.toFixed(2)} ${supplier.price_unit}`,
        supplier.notes,
      ].filter(Boolean).join(" · ");
      const created = await api.createSupplier(restaurantId, {
        name: supplier.name,
        contact_name: null,
        email: null,
        phone: null,
        address: null,
        notes,
      });
      setAdded(prev => new Set(prev).add(supplier.name));
      toast.success(`"${supplier.name}" añadido a tus proveedores`);
      onSupplierCreated?.(created);
    } catch (err) {
      toast.error(err?.message || "No se pudo añadir el proveedor");
    } finally {
      setAdding(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="supplier-suggestions-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-500" />
            Proveedores sugeridos
          </SheetTitle>
          <SheetDescription>
            3 proveedores reales para comprar <strong className="text-foreground">{ingredientName}</strong> + preguntas clave para negociar.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Analizando tu zona y cocina…
          </div>
        )}

        {data?.error && !loading && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            La IA no está disponible ahora, inténtalo de nuevo.
          </div>
        )}

        {data?.suggestions?.length > 0 && !loading && (
          <div className="mt-4 space-y-3">
            {data.suggestions.map((s, idx) => {
              const meta = TYPE_META[s.type] || TYPE_META.otros;
              const Icon = meta.icon;
              const isAdded = added.has(s.name);
              const isAdding = adding === s.name;
              return (
                <div
                  key={`${s.name}-${idx}`}
                  className="rounded-lg border border-border/60 bg-card p-3 space-y-2"
                  data-testid={`supplier-suggestion-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-sm">{s.name}</h4>
                        <Badge variant="outline" className={`mt-1 text-[10px] ${meta.color}`}>
                          {meta.label}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={isAdded ? "outline" : "default"}
                      disabled={isAdded || isAdding}
                      onClick={() => handleAdd(s)}
                      className="gap-1.5 flex-shrink-0"
                      data-testid={`supplier-suggestion-add-${idx}`}
                    >
                      {isAdded ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                       isAdding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                       <Plus className="w-3.5 h-3.5" />}
                      {isAdded ? "Añadido" : isAdding ? "Añadiendo…" : "Añadir a mi lista"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {s.specialty}
                  </p>
                  {(s.price_min > 0 || s.price_max > 0) && (
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="font-mono tabular-nums">
                        {s.price_min.toFixed(2)}–{s.price_max.toFixed(2)} {s.price_unit}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">precio esperado</span>
                    </div>
                  )}
                  {s.notes && (
                    <p className="text-xs text-foreground/80 leading-relaxed italic border-l-2 border-muted pl-2">
                      {s.notes}
                    </p>
                  )}
                </div>
              );
            })}

            {data.negotiation_checklist?.length > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-muted/40 border border-border/40" data-testid="supplier-negotiation-checklist">
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4 text-violet-500" />
                  Preguntas para negociar
                </h4>
                <ul className="space-y-1.5">
                  {data.negotiation_checklist.map((q, i) => (
                    <li key={i} className="text-xs text-foreground/85 flex items-start gap-1.5 leading-relaxed">
                      <span className="text-violet-500 flex-shrink-0">•</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data._provider && (
              <p className="text-[10px] text-muted-foreground text-right pt-2">
                {data._provider}
              </p>
            )}
          </div>
        )}

        <SheetFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="supplier-suggestions-close">
            Cerrar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
